import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { useAuth } from '../contexts/auth/hooks';
import { useSubscription } from '../hooks/useSubscription';

import Login from '../pages/Login';
import { RegistrationWizard } from '../components/registration/RegistrationWizard';
import { RegistrationProvider } from '../contexts/registration/provider';
import Dashboard from '../pages/Dashboard';
import UpdatePassword from '../pages/Login/UpdatePassword';
import ForgotPassword from '../pages/Login/ForgotPassword';
import MCPAgentChat from '../pages/MCPAgentChat/MCPAgentChat';
import AIAssistantPage from '../pages/AIAssistant';
import AIAssistantV3 from '../pages/AIAssistantV3';
import PlanSelection from '../components/registration/steps/business/components/PlanSelection';
import { NewAppointmentFlow } from '../components/appointments/NewAppointmentFlow';
import Customers from '../pages/Customers';
import Settings from '../pages/Settings';
import BusinessHours from '../pages/Settings/BusinessHours';
import BusinessServices from '../pages/Settings/BusinessServices';
import BusinessProducts from '../pages/Settings/BusinessProducts';
import BusinessStaff from '../pages/Settings/BusinessStaff';
import BusinessSettings from '../pages/Settings/BusinessSettings';
import LoyaltySettings from '../pages/Settings/LoyaltySettings';
import NotificationSettings from '../pages/Settings/NotificationSettings';
import ExternalPageSettings from '../pages/Settings/ExternalPageSettings';
import ExternalPage from '../pages/ExternalPage';
import Statistics from '../pages/Statistics';
import Invoices from '../pages/Settings/Invoices';
import CalendarPage from '../pages/Calendar/CalendarPage';
import TerminalForm from '../components/TerminalForm';
import Support from '../pages/Support';
import WhatsAppSettings from '../pages/Settings/WhatsApp';
import PlanUpgrade from '../pages/PlanUpgrade';
import SubscriptionManagement from '../pages/SubscriptionManagement';
import AuthCallback from '../pages/AuthCallback';
import WelcomeBoot from '../pages/WelcomeBoot';
import OnboardingComplete from '../pages/OnboardingComplete';
import ProtectedRoute from './ProtectedRoute';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsOfService from '../pages/TermsOfService';
import ExternalPrivacyPolicy from '../pages/ExternalPage/PrivacyPolicy';
import ExternalTermsOfService from '../pages/ExternalPage/TermsOfService';
import Consent from '../pages/Consent';
import RequireConsent from '../components/RequireConsent';
import LandingPage from '../pages/LandingPage';

function AppRoutes() {
  const { user } = useAuth();

  const RegistrationRoutes = () => (
    <RegistrationProvider>
      <RegistrationWizard />
    </RegistrationProvider>
  );

  // עטיפת פיצ'ר לפי חבילה (ללא שינוי)
  const FeatureProtectedRoute = ({
    children,
    featureCode,
    fallbackPath = '/settings',
  }: {
    children: React.ReactNode;
    featureCode: string;
    fallbackPath?: string;
  }) => {
    const { isFeatureAvailable } = useSubscription();
    if (featureCode === 'loyalty_program') return <>{children}</>;
    if (!isFeatureAvailable(featureCode)) return <Navigate to={fallbackPath} replace />;
    return <>{children}</>;
  };

  // NewAppointment popup route
  const NewAppointmentRoute = () => (
    <NewAppointmentFlow onClose={() => window.history.back()} onSuccess={() => window.history.back()} />
  );

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" replace />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/register" element={<Navigate to="/register/business" replace />} />
      <Route path="/register/business" element={<RegistrationRoutes />} />
      <Route path="/register/staff" element={<RegistrationRoutes />} />
      <Route path="/update-password" element={<UpdatePassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/book/:businessLink" element={<ExternalPage />} />
      <Route path="/book/privacy-policy" element={<ExternalPrivacyPolicy />} />
      <Route path="/book/terms-of-service" element={<ExternalTermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/consent" element={<Consent />} />
      {/* Boot (מחייב user, אך לא מחייב business) */}
      <Route
        path="/welcome/boot"
        element={
          <ProtectedRoute>
            <WelcomeBoot />
          </ProtectedRoute>
        }
      />
      {/* Onboarding Complete (after Stripe payment) */}
      <Route
        path="/welcome/onboarding-complete"
        element={
          <ProtectedRoute>
            <OnboardingComplete />
          </ProtectedRoute>
        }
      />

      {/* Protected */}
      <Route
        path="/dashboard"
        element={
    <ProtectedRoute>
      <RequireConsent>
        <Dashboard />
      </RequireConsent>
    </ProtectedRoute>
  }
      />
      <Route
        path="/mcp-agent"
        element={
          <ProtectedRoute>
            <MCPAgentChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-assistant"
        element={
          <ProtectedRoute>
            <AIAssistantPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-assistant-v3"
        element={
          <ProtectedRoute>
            <AIAssistantV3 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/statistics"
        element={
          <ProtectedRoute>
            <Statistics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments/new"
        element={
          <ProtectedRoute>
            <NewAppointmentRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/*"
        element={
          <ProtectedRoute>
            <Customers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/business"
        element={
          <ProtectedRoute>
            <BusinessSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/hours"
        element={
          <ProtectedRoute>
            <BusinessHours />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/services"
        element={
          <ProtectedRoute>
            <BusinessServices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/products"
        element={
          <ProtectedRoute>
            <BusinessProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/staff"
        element={
          <ProtectedRoute>
            <BusinessStaff />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <NotificationSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/external-page"
        element={
          <ProtectedRoute>
            <ExternalPageSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/loyalty"
        element={
          <ProtectedRoute>
            <FeatureProtectedRoute featureCode="loyalty_program">
              <LoyaltySettings />
            </FeatureProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/invoices"
        element={
          <ProtectedRoute>
            <Invoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/terminal-form"
        element={
          <ProtectedRoute>
            <TerminalForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/whatsapp"
        element={
          <ProtectedRoute>
            <WhatsAppSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <Support />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plan-upgrade"
        element={
          <ProtectedRoute>
            <PlanUpgrade />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionManagement />
          </ProtectedRoute>
        }
      />

      {/* Registration standalone page (בחירת חבילה) */}
      <Route
        path="/registration/plan-selection"
        element={
          <RegistrationProvider>
            <PlanSelection />
          </RegistrationProvider>
        }
      />

      {/* Default & 404 */}
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default AppRoutes;
